import React from "react";
import {
  IconExternalLink,
  IconFileDownload,
  IconLayoutSidebarRightCollapse,
} from "@tabler/icons-react";
import clsx from "clsx";
import Tooltip from "components/Tooltip";
import Button from "components/Button";
import { useTranslation } from "react-i18next";
import GeneralInfo from "./GeneralInfo";
import ExternalLinks from "./ExternalLinks";
import Conformity from "./Conformity";
import ReferenceValues from "./ReferenceValues";
import EnvironmentalIndicators from "./EnvironmentalIndicators";
import {
  EPDDetailOUT,
  EPDSearchOUT,
  Material2050SearchOUT,
  Materials2050EPDDetailOUT,
} from "api-client";
import { epdSearchStore } from "features/EPDSearchModal/EPDSearchStore";
import LoadingView from "../../components/LoadingView";
import EmptyView from "../../components/EmptyView";
import { observer } from "mobx-react-lite";
import { LoadingState } from "../EPDSearch2050/SearchTable";
import Alert from "components/Alert";
import useToast from "hooks/useToast";
import ConfirmModal from "components/ConfirmModal";
import { globalStore } from "store/GlobalStore";
import DocumentsTable from "./DocumentsTable";
import { useNavigate } from "react-router";
import { ROUTES_ENUM } from "routes/consts";
import { BPEStore } from "features/BuildingProductEditor/ProductEditorStore";
import { ProductActionMode } from "utils";

const EPDOverview = (props: {
  epdID?: string | null | undefined;
  epd?: Material2050SearchOUT | EPDSearchOUT | null;
  close: () => void;
  handleSelectEPD?: (epd: Material2050SearchOUT | EPDSearchOUT) => void;
  loadingEPDSelect?: LoadingState;
  generate?: boolean;
}) => {
  const epdID = props.epd?.id || props.epdID;
  const { t } = useTranslation();

  const scrollAreaRef = React.useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = React.useState(false);
  const [details, setDetails] = React.useState<
    Materials2050EPDDetailOUT | EPDDetailOUT | null
  >(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<number>();
  const toast = useToast();
  const [openRequestEPDAlert, setOpenRequestEPDAlert] = React.useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    getEPDDetails();
    scrollAreaRef.current?.scrollTo(0, 0);
  }, [epdID]);

  async function getEPDDetails() {
    if (!epdID) return;
    setLoading(true);
    try {
      const response = epdSearchStore.isExternalSearch
        ? await epdSearchStore.fetch2050EPDDetails(epdID)
        : await epdSearchStore.fetchEPDDetails(epdID);
      if (!response) return;
      setDetails(response);
      setError(undefined);
    } catch (error) {
      if (error && typeof error === "object" && "status" in error) {
        setError((error as { status: number }).status);
      }
    } finally {
      setLoading(false);
    }
  }

  const onScrollDiv = () => {
    setIsSticky(scrollAreaRef.current?.scrollTop !== 0);
  };

  function closeOverviewButton() {
    return (
      <Button
        leadingIcon={<IconLayoutSidebarRightCollapse />}
        width="fit-content"
        type="gray"
        className="mt-8"
        onClick={props.close}
      >
        {t("productEditor.closeOverview")}
      </Button>
    );
  }

  function onSelectEPD() {
    if (!props.epd) return;
    if (epdSearchStore.isExternalSearch) {
      (details as Materials2050EPDDetailOUT)?.is_good_quality
        ? props?.epd && props.handleSelectEPD?.(props.epd)
        : handleRequestEPD(details);
    } else {
      props.handleSelectEPD?.(props.epd);
    }
  }

  const onEditClicked = () => {
    navigate(`${ROUTES_ENUM.EPD_EDITOR}`, {
      state: {
        id: props?.epd?.id,
        mode: ProductActionMode.Edit,
      },
    });
  };

  async function handleRequestEPD(
    details: Materials2050EPDDetailOUT | EPDDetailOUT | null
  ) {
    if (!details?.external_links?.epd_source) return;
    await BPEStore.submitCustomEPDRequest(details?.external_links?.epd_source)
      .then(() => {
        setOpenRequestEPDAlert(true);
      })
      .catch((error) =>
        toast(
          t("productEditor.requestEPDFaild", { error_code: error.status }),
          "warning"
        )
      );
  }

  const onGenerateClicked = () => {
    navigate(`${ROUTES_ENUM.PRODUCT_EDITOR}`, {
      state: {
        id: props.epd?.id,
        mode: "generate",
        isExternal: epdSearchStore.isExternalSearch,
      },
    });
  };

  function headerTile() {
    return (
      <div
        className={clsx(
          "py-3 px-6 flex items-center justify-between sticky top-0 z-10 bg-white",
          { "shadow-md": isSticky }
        )}
      >
        {showEPDName()}
        <div className={clsx("flex items-center right-2 text-indigo-700 ml-2")}>
          {Boolean(
            details?.external_links?.epd_source ??
              details?.external_links?.user_document
          ) && (
            <IconFileDownload
              stroke={2}
              className="ml-2 cursor-pointer"
              onClick={() =>
                window.open(
                  String(
                    details?.external_links?.epd_source ??
                      details?.external_links?.user_document
                  ),
                  "_blank"
                )
              }
            />
          )}
          {details?.external_links?.materials_2050_source && (
            <IconExternalLink
              stroke={2}
              className="ml-2 cursor-pointer"
              onClick={() =>
                window.open(
                  String(details.external_links?.materials_2050_source),
                  "_blank"
                )
              }
            />
          )}
          {props.handleSelectEPD &&
            !epdSearchStore.isOrganisationalSearch &&
            !props.generate && (
              <Button
                width="fit-content"
                type="primary"
                className="ml-3"
                size="default"
                onClick={onSelectEPD}
                loading={
                  props.loadingEPDSelect?.state &&
                  props.loadingEPDSelect?.id === props.epd?.id
                }
                disable={props.loadingEPDSelect?.state}
              >
                {epdSearchStore.isExternalSearch &&
                !(details as Materials2050EPDDetailOUT)?.is_good_quality
                  ? t("commons.request")
                  : t("commons.select")}
              </Button>
            )}
          {props.generate && (
            <Button
              width="fit-content"
              type="primary"
              className="ml-3 whitespace-nowrap"
              size="default"
              onClick={onGenerateClicked}
              loading={
                props.loadingEPDSelect?.state &&
                props.loadingEPDSelect?.id === props.epd?.id
              }
              disable={props.loadingEPDSelect?.state}
            >
              {t("productEditor.generateProduct")}
            </Button>
          )}
          {epdSearchStore.isOrganisationalSearch && (
            <Button
              width="fit-content"
              type="primary"
              className="ml-3"
              size="default"
              onClick={onEditClicked}
              disable={!(props.epd as EPDSearchOUT).is_editable}
              tooltip={
                (props.epd as EPDSearchOUT).is_editable
                  ? ""
                  : t("EPDEditor.disabledEdit")
              }
            >
              {t("EPDEditor.editEPD")}
            </Button>
          )}
        </div>
      </div>
    );

    function showEPDName() {
      const epdName = epdSearchStore.isExternalSearch
        ? (props.epd as Material2050SearchOUT).name
        : globalStore.currentLanguage === "de"
        ? (props.epd as EPDSearchOUT)?.name_de
        : (props.epd as EPDSearchOUT)?.name_en;

      return (
        <div className="flex items-center">
          <IconLayoutSidebarRightCollapse
            size={30}
            className="text-indigo-700 cursor-pointer mr-4 min-w-[23px]"
            onClick={props.close}
          />
          {epdName && (
            <Tooltip
              className={clsx(
                "font-semibold text-lg !cursor-auto text-left",
                props.generate ? "max-w-[250px]" : "max-w-[320px]"
              )}
              truncate
            >
              {epdName}
            </Tooltip>
          )}
        </div>
      );
    }
  }

  if (!epdID) return null;
  if (error)
    return (
      <EmptyView
        errorCode={error}
        message={
          error
            ? t("productEditor.overviewErrorMessage")
            : t("productEditor.noEPDFound")
        }
        button={closeOverviewButton()}
      />
    );
  if (loading)
    return (
      <LoadingView
        message={t("productEditor.EPDInformationLoading")}
        className="!w-20 !h-20"
        button={closeOverviewButton()}
      />
    );

  return (
    <div
      className={clsx(
        "col-span-1 max-h-[calc(100vh-238px)] h-[calc(100vh-238px)] overflow-y-auto"
      )}
      ref={scrollAreaRef}
      onScroll={onScrollDiv}
    >
      {props.epd ? (
        headerTile()
      ) : (
        <div className="pt-6 pb-3 px-6 flex items-center justify-between z-10 bg-white font-semibold">
          {details?.name_de || details?.name_en}
        </div>
      )}
      <div className="py-3 px-6">
        {epdSearchStore.isExternalSearch &&
          !(details as Materials2050EPDDetailOUT)?.is_good_quality && (
            <Alert
              type="attention"
              title={t("productEditor.lowQualityEPDTitle")}
              description={t("productEditor.lowQualityEPDMessage")}
              className="mb-6"
            />
          )}
        <GeneralInfo details={details} />
        <ExternalLinks details={details} />
        {details?.documents?.length ? (
          <DocumentsTable documents={details?.documents} />
        ) : null}
        <Conformity details={details} />
        <ReferenceValues details={details} />
        <EnvironmentalIndicators details={details} />
      </div>
      <ConfirmModal
        type="success"
        open={openRequestEPDAlert}
        close={() => setOpenRequestEPDAlert(false)}
        title={t("productEditor.epdAskedSuccessfully")}
        descriptionMesage={t("productEditor.epdAskedSuccessfullyMessage")}
        onConfirm={() => setOpenRequestEPDAlert(false)}
        confirmButtonTitle={t("productEditor.understood")}
        hideCancel
      />
    </div>
  );
};

export default observer(EPDOverview);
